/* eslint-disable @typescript-eslint/naming-convention */
import { FlexLayoutModule } from '@angular/flex-layout';
import { BrowserModule } from '@angular/platform-browser';
import {APP_INITIALIZER, NgModule} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ZonarUICoreModule } from '@zonar-ui/core';
import { ZonarUiProgressSpinnerModule } from '@zonar-ui/progress-spinner';
import {
    ZonarUiHosNavigationModule,
    HOS_APP_URL,
    HOS_ENV,
    HOS_APP_APPLICATION_IDS,
    HOS_APP_NAME,
    HOS_APP_DEV_CYCLE_CLIENT_ID
} from '@zonar-ui/hos-navigation';
import { CoreModule } from 'src/app/core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import {
    AuthInterceptor,
    PXInitService,
    ZonarOwnerIdInterceptor,
    ZonarUiAuthModule,
    ZonarUiPermissionsModule
} from '@zonar-ui/auth';
import { AuthHttpInterceptor, AuthModule, AuthService} from '@auth0/auth0-angular';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgxPendoModule } from 'ngx-pendo';
import { reducers } from './modules/udl/state';
import {appNameForPerm, permissions} from '../environments/shared';
import { AppRootComponent } from './app-root.component';
import { ZonarUiI18nModule } from '@zonar-ui/i18n';
import { TranslateModule } from '@ngx-translate/core';
import {ZonarUiHosComponentsModule} from '@zonar-ui/hos-components';
import { ZonarUiFooterModule } from '@zonar-ui/footer';
import { SidenavModule } from '@zonar-ui/sidenav';
import {MatIconModule} from '@angular/material/icon';

//PX INITIALIZATION SERVICE for users logged in from Px Platform
export const startupFactory = (initService: PXInitService) =>  (
    () => initService.init().then(value => {
            console.log('PxInitService Initializing here in ELDX app module', value);
        })
);

//urls related to GPS trackit that allow the token exchange from Px platform
const gpsTrackitUrls = [
    'https://gpstrackit.com',
    'http://localhost:40000',
    'https://scr.gpstrackit.com',
    'https://cloud.gpstrackit.com',
    'https://ignition.gpstrackit.com',
    'https://cloud.zonarsystems.com',
    'https://ignition.zonarsystems.com',
    'https://cloud.zonar.com',
    'https://ignition.zonar.com'];


const allowLists = [
    environment.urls.baseApi + '/*',
    environment.urls.udlApi + '/*',
    environment.urls.externalApi + '/*',
    environment.urls.driverLogsApi + '/*',
    environment.urls.legacyEntityApi + '/*',
    environment.urls.hosReportApi + '/*',
    environment.auth.domain,
    environment.urls.complianceUrl + '/*',
];

@NgModule({
    declarations: [
        AppRootComponent,
        AppComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        TranslateModule.forRoot({}),
        AuthModule.forRoot({
            clientId: environment.auth.clientID,
            domain: environment.auth.domain,
            audience: environment.auth.audience,
            redirect_uri: window.location.origin,
            scope: 'openid profile email',
            useRefreshTokens: true,
            max_age: 36000,
            cacheLocation: 'localstorage',
            httpInterceptor: {
                allowedList: [
                    environment.urls.baseApi + '/*',
                    environment.urls.udlApi + '/*',
                    environment.urls.externalApi + '/*',
                    environment.urls.driverLogsApi + '/*',
                    environment.urls.legacyEntityApi + '/*',
                    environment.urls.hosReportApi + '/*',
                    environment.auth.domain,
                    environment.appUrl + '/*',
                    environment.urls.complianceUrl + '/*',
                    'https://support.zonarsystems.net/*'
                ]
            }
        }),
        CoreModule,
        AppRoutingModule,
        ZonarUiI18nModule,
        ZonarUICoreModule,
        ZonarUiAuthModule,
        FlexLayoutModule,
        ZonarUiProgressSpinnerModule,
        ZonarUiHosNavigationModule,
        StoreModule.forRoot(reducers, {}),
        StoreDevtoolsModule.instrument({maxAge: 25, logOnly: environment.production}),
        EffectsModule.forRoot([]),
        StoreRouterConnectingModule.forRoot(),
        NgxPendoModule.forRoot({
            pendoApiKey: '47e63a49-4477-47c2-4ea6-0f032e1785d5',
            pendoIdFormatter: (value: any) => value.toString().toLowerCase()
        }),
        HttpClientModule,
        ZonarUiPermissionsModule,
        ZonarUiHosComponentsModule,
        ZonarUiFooterModule,
        SidenavModule.forRoot({stage: environment.authEnv}),
        MatIconModule
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: startupFactory,
            deps: [PXInitService,
                // LocalStateService
            ],
            multi: true,
        },
        AuthService,
        AuthModule,
        ZonarUiPermissionsModule,
        {provide: 'applicationId', useValue: environment.auth.applicationId},
        {provide: 'environment', useValue: environment.authEnv},
        {provide: HOS_ENV, useValue: environment.name},
        {provide: HOS_APP_NAME, useValue: appNameForPerm},
        {
            provide: HOS_APP_URL, useValue: {
                eldx: environment.appUrl,
                compliance: environment.urls.complianceUrl
            }
        },
		{
			provide: HOS_APP_APPLICATION_IDS, useValue: {
				eldx: environment.auth.applicationId,
				compliance: environment.complianceApplicationId,
			}
		},
        {
            provide: HOS_APP_DEV_CYCLE_CLIENT_ID,
            useValue: environment.devCycle.clientId
        },
        {provide: 'defaultPermPrefix', useValue: permissions.defaultPermPrefix},
        {provide: 'routePermissions', useValue: permissions.routes},
        {provide: 'redirectUrl', useValue: permissions.defaultRedirectUrl},
        {provide: 'region', useValue: environment.region},
        {
            provide: HTTP_INTERCEPTORS,
            useClass: new URLSearchParams(window.location.search).get('pxLogin')?.toLocaleLowerCase() === 'true'
                ? AuthInterceptor
                : AuthHttpInterceptor,
            multi: true,
        },
        {provide: HTTP_INTERCEPTORS, useClass: ZonarOwnerIdInterceptor, multi: true},
        {provide: 'supportedLanguages', useValue: ['en-US']},
        {provide: 'defaultLanguage', useValue: 'en-US'},
        {provide: 'allowedOrigins',
            useValue: [...gpsTrackitUrls, 'http://localhost:4200', environment.auth.domain+ '/*', environment.appUrl+ '/*'],
        },
        {
          provide: 'allowedUrls', useValue: [...allowLists, environment.appUrl + '/*']
        },
        ZonarUiAuthModule
    ],
    bootstrap: [AppRootComponent],
    exports: []
})
export class AppModule { }
